export enum StripeElement {
  Address = 'Address',
  Payment = 'Payment',
}

// Crawl from https://docs.stripe.com/error-codes
export const ERRORS = [
  {
    code: 'incomplete_name',
    belongsTo: StripeElement.Address,
  },
  {
    code: 'empty_phone_number',
    belongsTo: StripeElement.Address,
  },
  {
    code: 'account_closed',
    belongsTo: StripeElement.Payment,
    description: 'The customer’s bank account has been closed.',
  },

  {
    code: 'account_country_invalid_address',
    description:
      'The country of the business address provided does not match the country of the account. Businesses must be located in the same country as the account.',
  },

  {
    code: 'account_error_country_change_requires_additional_steps',
    description:
      'Your account has already onboarded as a Connect platform. Changing your country requires additional steps. Please reach out to Stripe support for more information.',
  },

  {
    code: 'account_information_mismatch',
    description:
      'Some account information mismatches with one another. For example, some banks might require that the business_profile.name must match the account holder name.',
  },

  {
    code: 'account_invalid',
    description:
      'The account ID provided as a value for the Stripe-Account header is invalid. Check that your requests are specifying a valid account ID.',
  },

  {
    code: 'account_number_invalid',
    description:
      'The bank account number provided is invalid (e.g., missing digits). Bank account information varies from country to country. We recommend creating validations in your entry forms based on the bank account formats we provide.',
  },

  {
    code: 'acss_debit_session_incomplete',
    description:
      'The ACSS debit session is not ready to transition to complete status yet. Please try again the request later.',
  },

  {
    code: 'alipay_upgrade_required',
    description:
      'This method for creating Alipay payments is not supported anymore. Please upgrade your integration to use Sources instead.',
  },

  {
    code: 'amount_too_large',
    description:
      'The specified amount is greater than the maximum amount allowed. Use a lower amount and try again.',
  },

  {
    code: 'amount_too_small',
    description:
      'The specified amount is less than the minimum amount allowed. Use a higher amount and try again.',
  },

  {
    code: 'api_key_expired',
    description:
      'The API key provided has expired. Obtain your current API keys from the Dashboard and update your integration to use them.',
  },

  {
    code: 'application_fees_not_allowed',
    description: 'The proposed money movement could not be completed due to regulatory reasons.',
  },

  {
    code: 'authentication_required',
    description:
      'The payment requires authentication to proceed. If your customer is off session, notify your customer to return to your application and complete the payment. If you provided the error_on_requires_action parameter, then your customer should try another card that does not require authentication.',
  },

  {
    code: 'balance_insufficient',
    description:
      'The transfer or payout could not be completed because the associated account does not have a sufficient balance available. Create a new transfer or payout using an amount less than or equal to the account’s available balance.',
  },

  {
    code: 'balance_invalid_parameter',
    description:
      'Invalid parameter was provided in the balance method object. Check our API documentation or the returned error message for more context.',
  },

  {
    code: 'bank_account_bad_routing_numbers',
    description: 'The bank account is known to not support the currency in question.',
  },

  {
    code: 'bank_account_declined',
    description:
      'The bank account provided can not be used to charge, either because it is not verified yet or it is not supported.',
  },

  {
    code: 'bank_account_exists',
    description:
      'The bank account provided already exists on the specified Customer object. If the bank account should also be attached to a different customer, include the correct customer ID when making the request again.',
  },

  {
    code: 'bank_account_restricted',
    description: 'The customer’s account cannot be used with the payment method.',
  },

  {
    code: 'bank_account_unusable',
    description: 'The bank account provided cannot be used. A different bank account must be used.',
  },

  {
    code: 'bank_account_unverified',
    description:
      'Your Connect platform is attempting to share an unverified bank account with a connected account.',
  },

  {
    code: 'bank_account_verification_failed',
    description:
      'The bank account cannot be verified, either because the microdeposit amounts provided do not match the actual amounts, or because verification has failed too many times.',
  },

  {
    code: 'billing_invalid_mandate',
    description:
      'The Subscription or Invoice attempted payment on a PaymentMethod without an active mandate. In order to create Subscription or Invoice payments with this PaymentMethod, it must be confirmed on-session with a PaymentIntent or SetupIntent first.',
  },

  {
    code: 'bitcoin_upgrade_required',
    description:
      'This method for creating Bitcoin payments is not supported anymore. Please upgrade your integration to use Sources instead.',
  },

  {
    code: 'capture_charge_authorization_expired',
    description:
      'The charge cannot be captured as the authorization has expired. Refer to the payment method’s documentation to learn more.',
  },

  {
    code: 'capture_unauthorized_payment',
    description:
      'The charge you’re attempting to capture has not been authorized for capturing payment.',
  },

  {
    code: 'card_decline_rate_limit_exceeded',
    description:
      'This card has been declined too many times. You can try to charge this card again after 24 hours. We suggest reaching out to your customer to make sure they have entered all of their information correctly and that there are no issues with their card.',
  },

  {
    code: 'card_declined',
    description:
      'The card has been declined. When a card is declined, the error returned also includes the decline_code attribute with the reason why the card was declined. Refer to our decline codes documentation to learn more.',
  },

  {
    code: 'cardholder_phone_number_required',
    description:
      'You must have a phone_number on file for Issuing Cardholders who will be creating EU cards. You cannot create EU cards without a phone_number on file for the cardholder. See the 3D Secure Documenation for more details.',
  },

  {
    code: 'charge_already_captured',
    description:
      'The charge you’re attempting to capture has already been captured. Update the request with an uncaptured charge ID.',
  },

  {
    code: 'charge_already_refunded',
    description:
      'The charge you’re attempting to refund has already been refunded. Update the request to use the ID of a charge that has not been refunded.',
  },

  {
    code: 'charge_disputed',
    description:
      'The charge you’re attempting to refund has been charged back. Check the disputes documentation to learn how to respond to the dispute.',
  },

  {
    code: 'charge_exceeds_source_limit',
    description:
      'This charge would cause you to exceed your rolling-window processing limit for this source type. Please retry the charge later, or contact us to request a higher processing limit.',
  },

  {
    code: 'charge_exceeds_transaction_limit',
    description:
      'This charge would cause you to exceed your processing limit for this payment type. Please contact us to request a higher processing limit.',
  },

  {
    code: 'charge_expired_for_capture',
    description:
      'The charge cannot be captured as the authorization has expired. Auth and capture charges must be captured within a set number of days (7 by default).',
  },

  {
    code: 'charge_invalid_parameter',
    description:
      'One or more provided parameters was not allowed for the given operation on the Charge. Check our API reference or the returned error message to see which values were not correct for that Charge.',
  },

  {
    code: 'charge_not_refundable',
    description:
      'Attempt to refund a charge was unsuccessful because the charge is no longer refundable.',
  },

  {code: 'clearing_code_unsupported', description: 'The clearing code provided is not supported.'},

  {code: 'country_code_invalid', description: 'The country code provided was invalid.'},

  {
    code: 'country_unsupported',
    description:
      'Your platform attempted to create a custom account in a country that is not yet supported. Make sure that users can only sign up in countries supported by custom accounts.',
  },

  {
    code: 'coupon_expired',
    description:
      'The coupon provided for a subscription or order has expired. Either create a new coupon, or use an existing one that is valid.',
  },

  {
    code: 'customer_max_payment_methods',
    description:
      'The maximum number of PaymentMethods for this Customer has been reached. Either detach some PaymentMethods from this Customer or proceed with a different Customer.',
  },

  {
    code: 'customer_max_subscriptions',
    description:
      'The maximum number of subscriptions for a customer has been reached. Contact us if you are receiving this error.',
  },

  {
    code: 'customer_tax_location_invalid',
    description:
      'The customer address is missing or not valid for tax purposes. Make sure to provide a country as a two-letter ISO code and also a postal_code, at minimum. See how to collect customer addresses.',
  },

  {
    code: 'debit_not_authorized',
    description: 'The customer has notified their bank that this payment was unauthorized.',
  },

  {
    code: 'email_invalid',
    description:
      'The email address is invalid (e.g., not properly formatted). Check that the email address is properly formatted and only includes allowed characters.',
  },

  {
    code: 'expired_card',
    description: 'The card has expired. Check the expiration date or use a different card.',
  },

  {
    code: 'financial_connections_account_inactive',
    description: 'Data cannot be refreshed on inactive Financial Connections accounts.',
  },

  {
    code: 'financial_connections_no_successful_transaction_refresh',
    description:
      'Transaction data can only be retrieved for accounts that have at least one successful transaction refresh.',
  },

  {
    code: 'forwarding_api_inactive',
    description:
      'The vault and forward API is currently not accessible with this account and/or config. Please contact us if you are receiving this error.',
  },

  {
    code: 'forwarding_api_invalid_parameter',
    description:
      'Invalid parameter was provided in the vault and forward API. Check our API documentation or the returned error message for more context.',
  },

  {
    code: 'forwarding_api_retryable_upstream_error',
    description:
      'The destination endpoint appears to be offline or unresponsive, preventing Stripe from completing the request. Please retry the request later.',
  },

  {
    code: 'forwarding_api_upstream_connection_error',
    description:
      'Stripe did not receive a response from the destination endpoint. This typically indicates a problem with the destination endpoint, rather than with Stripe.',
  },

  {
    code: 'forwarding_api_upstream_connection_timeout',
    description:
      'The request to the destination endpoint timed out. This typically indicates a problem with the destination endpoint, rather than with Stripe.',
  },

  {
    code: 'idempotency_key_in_use',
    description:
      'The idempotency key provided is currently being used in another request. This occurs if your integration is making duplicate requests simultaneously.',
  },

  {
    code: 'incorrect_address',
    description:
      'The card’s address is incorrect. Check the card’s address or use a different card.',
  },

  {
    code: 'incorrect_cvc',
    description:
      'The card’s security code is incorrect. Check the card’s security code or use a different card.',
  },

  {
    code: 'incorrect_number',
    description: 'The card number is incorrect. Check the card’s number or use a different card.',
  },

  {
    code: 'incorrect_zip',
    description:
      'The card’s postal code is incorrect. Check the card’s postal code or use a different card.',
  },

  {
    code: 'instant_payouts_config_disabled',
    description:
      'This connected account is not eligible for Instant Payouts. Ask the platform to enable Instant Payouts.',
  },

  {
    code: 'instant_payouts_currency_disabled',
    description:
      'This connected account is not eligible for Instant Payouts in this currency. Ask the platform to enable Instant Payouts in this currency.',
  },

  {
    code: 'instant_payouts_limit_exceeded',
    description: 'You have reached your daily processing limits for Instant Payouts.',
  },

  {
    code: 'instant_payouts_unsupported',
    description:
      'Instant Payouts are not available for this request. Please review our Instant Payouts documentation for detailed requirements and supported configurations.',
  },

  {
    code: 'insufficient_funds',
    description: 'The customer’s account has insufficient funds to cover this payment.',
  },

  {
    code: 'intent_invalid_state',
    description: 'Intent is not in the state that is required to perform the operation.',
  },

  {
    code: 'intent_verification_method_missing',
    description:
      'Intent does not have verification method specified in its PaymentMethodOptions object.',
  },

  {
    code: 'invalid_card_type',
    description:
      'The card provided as an external account is not supported for payouts. Provide a non-prepaid debit card instead.',
  },

  {
    code: 'invalid_characters',
    description:
      'This value provided to the field contains characters that are unsupported by the field.',
  },

  {
    code: 'invalid_charge_amount',
    description:
      'The specified amount is invalid. The charge amount must be a positive integer in the smallest currency unit, and not exceed the minimum or maximum amount.',
  },

  {
    code: 'invalid_cvc',
    description:
      'The card’s security code is invalid. Check the card’s security code or use a different card.',
  },

  {
    code: 'invalid_expiry_month',
    description:
      'The card’s expiration month is incorrect. Check the expiration date or use a different card.',
  },

  {
    code: 'invalid_expiry_year',
    description:
      'The card’s expiration year is incorrect. Check the expiration date or use a different card.',
  },

  {
    code: 'invalid_mandate_reference_prefix_format',
    description: 'The provided prefix used to generate the mandate reference is invalid.',
  },

  {
    code: 'invalid_number',
    description: 'The card number is invalid. Check the card details or use a different card.',
  },

  {
    code: 'invalid_source_usage',
    description:
      'The source cannot be used because it is not in the correct state (e.g., a charge request is trying to use a source with a pending, failed, or consumed source). Check the status of the source you are attempting to use.',
  },

  {
    code: 'invalid_tax_location',
    description:
      'The specified location is invalid. Check the Supported address formats for the address formats supported when calculating tax.',
  },

  {
    code: 'invoice_no_customer_line_items',
    description:
      'An invoice cannot be generated for the specified customer as there are no pending invoice items. Check that the correct customer is being specified or create any necessary invoice items first.',
  },

  {
    code: 'invoice_no_payment_method_types',
    description:
      'An invoice cannot be finalized because there are no payment method types available to process the payment. Your invoice template settings or the invoice’s payment_settings might be restricting which payment methods are available, or you might need to activate more payment methods in the Dashboard.',
  },

  {
    code: 'invoice_no_subscription_line_items',
    description:
      'An invoice cannot be generated for the specified subscription as there are no pending invoice items. Check that the correct subscription is being specified or create any necessary invoice items first.',
  },

  {
    code: 'invoice_not_editable',
    description:
      'The specified invoice can no longer be edited. Instead, consider creating additional invoice items that will be applied to the next invoice. You can either manually generate the next invoice or wait for it to be automatically generated at the end of the billing cycle.',
  },

  {
    code: 'invoice_on_behalf_of_not_editable',
    description:
      'You cannot update the on_behalf_of property of an invoice after the invoice has been assigned a number.',
  },

  {
    code: 'invoice_payment_intent_requires_action',
    description:
      'This payment requires additional user action before it can be completed successfully. Payment can be completed using the PaymentIntent associated with the invoice. See this page for more details.',
  },

  {
    code: 'invoice_upcoming_none',
    description:
      'There is no upcoming invoice on the specified customer to preview. Only customers with active subscriptions or pending invoice items have invoices that can be previewed.',
  },

  {
    code: 'livemode_mismatch',
    description:
      'Test and live mode API keys, requests, and objects are only available within the mode they are in.',
  },

  {
    code: 'lock_timeout',
    description:
      'This object cannot be accessed right now because another API request or Stripe process is currently accessing it. If you see this error intermittently, retry the request. If you see this error frequently and are making multiple concurrent requests to a single object, make your requests serially or at a lower rate. See the rate limit documentation for more details.',
  },

  {
    code: 'missing',
    description:
      'Both a customer and source ID have been provided, but the source has not been saved to the customer. To create a charge for a customer with a specified source, you must first save the card details.',
  },

  {code: 'no_account', description: 'The bank account could not be located.'},

  {
    code: 'not_allowed_on_standard_account',
    description: 'Transfers and payouts on behalf of a Standard connected account are not allowed.',
  },

  {
    code: 'out_of_inventory',
    description:
      'One or more line item(s) are out of stock. If more stock is available, update the inventory’s orderable quantity and try again.',
  },

  {
    code: 'ownership_declaration_not_allowed',
    description:
      'Company ownership declaration is allowed only during account updates and accounts created via account tokens.',
  },

  {
    code: 'parameter_invalid_empty',
    description:
      'One or more required values were not provided. Make sure requests include all required parameters.',
  },

  {
    code: 'parameter_invalid_integer',
    description:
      'One or more of the parameters requires an integer, but the values provided were a different type. Make sure that only supported values are provided for each attribute. Refer to our API documentation to look up the type of data each attribute supports.',
  },

  {
    code: 'parameter_invalid_string_blank',
    description:
      'One or more values provided only included whitespace. Check the values in your request and update any that contain only whitespace.',
  },

  {
    code: 'parameter_invalid_string_empty',
    description:
      'One or more required string values is empty. Make sure that string values contain at least one character.',
  },

  {
    code: 'parameter_missing',
    description:
      'One or more required values are missing. Check our API documentation to see which values are required to create or modify the specified resource.',
  },

  {
    code: 'parameter_unknown',
    description:
      'The request contains one or more unexpected parameters. Remove these and try again.',
  },

  {
    code: 'parameters_exclusive',
    description:
      'Two or more mutually exclusive parameters were provided. Check our API documentation or the returned error message to see which values are permitted when creating or modifying the specified resource.',
  },

  {
    code: 'payment_intent_action_required',
    description:
      'The provided payment method requires customer actions to complete, but error_on_requires_action was set. If you’d like to add this payment method to your integration, we recommend that you first upgrade your integration to handle actions.',
  },

  {
    code: 'payment_intent_authentication_failure',
    description:
      'The provided payment method has failed authentication. Provide a new payment method to attempt to fulfill this PaymentIntent again.',
  },

  {
    code: 'payment_intent_incompatible_payment_method',
    description:
      'The PaymentIntent expected a payment method with different properties than what was provided.',
  },

  {
    code: 'payment_intent_invalid_parameter',
    description:
      'One or more provided parameters was not allowed for the given operation on the PaymentIntent. Check our API reference or the returned error message to see which values were not correct for that PaymentIntent.',
  },

  {
    code: 'payment_intent_konbini_rejected_confirmation_number',
    description:
      'The confirmation_number provided in payment_method_options[konbini] was rejected by the processing partner at time of PaymentIntent confirmation.',
  },

  {
    code: 'payment_intent_mandate_invalid',
    description: 'The provided mandate is invalid and can not be used for the payment intent.',
  },

  {
    code: 'payment_intent_payment_attempt_expired',
    description:
      'The latest payment attempt for the PaymentIntent has expired. Check the last_payment_error property on the PaymentIntent for more details, and provide a new payment method to attempt to fulfill this PaymentIntent again.',
  },

  {
    code: 'payment_intent_payment_attempt_failed',
    description:
      'The latest payment attempt for the PaymentIntent has failed. Check the last_payment_error property on the PaymentIntent for more details, and provide a new payment method to attempt to fulfill this PaymentIntent again.',
  },

  {
    code: 'payment_intent_unexpected_state',
    description:
      'The PaymentIntent’s state was incompatible with the operation you were trying to perform.',
  },

  {
    code: 'payment_method_bank_account_already_verified',
    description: 'This bank account has already been verified.',
  },

  {
    code: 'payment_method_bank_account_blocked',
    description:
      'This bank account has failed verification in the past and can not be used. Contact us if you wish to attempt to use these bank account credentials.',
  },

  {
    code: 'payment_method_billing_details_address_missing',
    description:
      'The PaymentMethod’s billing details is missing address details. Please update the missing fields and try again.',
  },

  {
    code: 'payment_method_configuration_failures',
    description: 'Attempt to create or modify Payment Method Configuration was unsuccessful.',
  },

  {
    code: 'payment_method_currency_mismatch',
    description:
      'The currency specified does not match the currency for the attached payment method. A payment can only be created for the same currency as the corresponding payment method.',
  },

  {
    code: 'payment_method_customer_decline',
    description:
      'The customer did not approve the payment. Please provide a new payment method to attempt to fulfill this intent again.',
  },

  {
    code: 'payment_method_invalid_parameter',
    description:
      'Invalid parameter was provided in the payment method object. Check our API documentation or the returned error message for more context.',
  },

  {
    code: 'payment_method_invalid_parameter_testmode',
    description:
      'The parameter provided for payment method is not allowed to be used in testmode. Check our API documentation or the returned error message for more context.',
  },

  {
    code: 'payment_method_microdeposit_failed',
    description:
      'Microdeposits were failed to be deposited into the customer’s bank account. Please check the account, institution and transit numbers as well as the currency type.',
  },

  {
    code: 'payment_method_microdeposit_verification_amounts_invalid',
    description: 'You must provide exactly two microdeposit amounts.',
  },

  {
    code: 'payment_method_microdeposit_verification_amounts_mismatch',
    description:
      'The amounts provided do not match the amounts that were sent to the bank account.',
  },

  {
    code: 'payment_method_microdeposit_verification_attempts_exceeded',
    description: 'You have exceeded the number of allowed verification attempts.',
  },

  {
    code: 'payment_method_microdeposit_verification_descriptor_code_mismatch',
    description: 'The verification code provided does not match the one sent to the bank account.',
  },

  {
    code: 'payment_method_microdeposit_verification_timeout',
    description: 'Payment method should be verified with microdeposits within the required period.',
  },

  {
    code: 'payment_method_not_available',
    description:
      'The payment processor for the provided payment method is temporarily unavailable. Please try a different payment method or retry later with the same payment method.',
  },

  {
    code: 'payment_method_provider_decline',
    description:
      'The payment or setup attempt was declined by the issuer or customer. Check the last_payment_error or last_setup_error property on the PaymentIntent or SetupIntent respectively for more details, and provide a new payment method to attempt to fulfill this intent again.',
  },

  {
    code: 'payment_method_provider_timeout',
    description:
      'The payment method failed due to a timeout. Check the last_payment_error or last_setup_error property on the PaymentIntent or SetupIntent respectively for more details, and provide a new payment method to attempt to fulfill this intent again.',
  },

  {
    code: 'payment_method_unactivated',
    description:
      'The operation cannot be performed as the payment method used has not been activated. Activate the payment method in the Dashboard, then try again.',
  },

  {
    code: 'payment_method_unexpected_state',
    description:
      'The provided payment method’s state was incompatible with the operation you were trying to perform. Confirm that the payment method is in an allowed state for the given operation before attempting to perform it.',
  },

  {
    code: 'payment_method_unsupported_type',
    description: 'The API only supports payment methods of certain types.',
  },

  {
    code: 'payout_reconciliation_not_ready',
    description: 'Reconciliation for this payout is still in progress.',
  },

  {
    code: 'payouts_limit_exceeded',
    description: 'You have reached your daily processing limits for this payout type.',
  },

  {
    code: 'payouts_not_allowed',
    description:
      'Payouts have been disabled on the connected account. Check the connected account’s status to see if any additional information needs to be provided, or if payouts have been disabled for another reason.',
  },

  {
    code: 'platform_account_required',
    description:
      'Only Stripe Connect platforms can work with other accounts. If you need to setup a Stripe Connect platform, you can do so in the dashboard.',
  },

  {
    code: 'platform_api_key_expired',
    description:
      'The API key provided by your Connect platform has expired. This occurs if your platform has either generated a new key or the connected account has been disconnected from the platform. Obtain your current API keys from the Dashboard and update your integration, or reach out to the user and reconnect the account.',
  },

  {code: 'postal_code_invalid', description: 'The postal code provided was incorrect.'},

  {
    code: 'processing_error',
    description:
      'An error occurred while processing the card. Try again later or with a different payment method.',
  },

  {
    code: 'product_inactive',
    description: 'The product this SKU belongs to is no longer available for purchase.',
  },

  {
    code: 'progressive_onboarding_limit_exceeded',
    description: 'Progressive onboarding limit has been reached for the platform.',
  },

  {
    code: 'rate_limit',
    description:
      'Too many requests hit the API too quickly. We recommend an exponential backoff of your requests.',
  },

  {
    code: 'refer_to_customer',
    description:
      'The customer has stopped the payment with their bank. Contact them for details and to arrange payment.',
  },

  {code: 'refund_disputed_payment', description: 'You cannot refund a disputed payment.'},

  {
    code: 'resource_already_exists',
    description:
      'A resource with a user-specified ID (e.g., plan or coupon) already exists. Use a different, unique value for id and try again.',
  },

  {
    code: 'resource_missing',
    description:
      'The ID provided is not valid. Either the resource does not exist, or an ID for a different resource has been provided.',
  },

  {
    code: 'return_intent_already_processed',
    description: 'You cannot confirm this refund as it is already processed.',
  },

  {code: 'routing_number_invalid', description: 'The bank routing number provided is invalid.'},

  {
    code: 'secret_key_required',
    description:
      'The API key provided is a publishable key, but a secret key is required. Obtain your current API keys from the Dashboard and update your integration to use them.',
  },

  {code: 'sepa_unsupported_account', description: 'Your account does not support SEPA payments.'},

  {
    code: 'setup_attempt_failed',
    description:
      'The latest setup attempt for the SetupIntent has failed. Check the last_setup_error property on the SetupIntent for more details, and provide a new payment method to attempt to set it up again.',
  },

  {
    code: 'setup_intent_authentication_failure',
    description:
      'The provided payment method has failed authentication. Provide a new payment method to attempt to fulfill this SetupIntent again.',
  },

  {
    code: 'setup_intent_invalid_parameter',
    description:
      'One or more provided parameters was not allowed for the given operation on the SetupIntent. Check our API reference or the returned error message to see which values were not correct for that SetupIntent.',
  },

  {
    code: 'setup_intent_mandate_invalid',
    description: 'The provided mandate is invalid and can not be used for the setup intent.',
  },

  {
    code: 'setup_intent_mobile_wallet_unsupported',
    description:
      'Card Present and Interac Present SetupIntents can only be created with physical cards, not with mobile wallets.',
  },

  {
    code: 'setup_intent_setup_attempt_expired',
    description:
      'The latest setup attempt for the SetupIntent has expired. Check the last_setup_error property on the SetupIntent for more details, and provide a new payment method to attempt to complete this SetupIntent again.',
  },

  {
    code: 'setup_intent_unexpected_state',
    description:
      'The SetupIntent’s state was incompatible with the operation you were trying to perform.',
  },

  {
    code: 'shipping_address_invalid',
    description:
      'The shipping address information cannot be used to accurately determine tax rates. Verify that address fields such as zip code, state, or province have been added correctly.',
  },

  {
    code: 'shipping_calculation_failed',
    description:
      'Shipping calculation failed as the information provided was either incorrect or could not be verified.',
  },

  {
    code: 'sku_inactive',
    description:
      'The SKU is inactive and no longer available for purchase. Use a different SKU, or make the current SKU active again.',
  },

  {
    code: 'state_unsupported',
    description:
      'Occurs when providing the legal_entity information for a U.S. custom account, if the provided state is not supported. (This is mostly associated states and territories.)',
  },

  {code: 'status_transition_invalid', description: 'The requested status transition is not valid.'},

  {
    code: 'stripe_tax_inactive',
    description:
      'Stripe Tax has not been activated on your account. Check the setup documentation to get started.',
  },

  {
    code: 'tax_id_invalid',
    description:
      'The tax ID number provided is invalid (e.g., missing digits). Tax ID information varies from country to country, but must be at least nine digits.',
  },

  {
    code: 'tax_id_prohibited',
    description:
      'A tax identifier may not be provided on this payment method as it is not required.',
  },

  {code: 'taxes_calculation_failed', description: 'Tax calculation for the order failed.'},

  {
    code: 'terminal_location_country_unsupported',
    description:
      'Terminal is currently only available in some countries. Locations in your country cannot be created in livemode.',
  },

  {
    code: 'terminal_reader_busy',
    description:
      'Reader is currently busy processing another request. Please reference the integration guide for details on how to handle this error.',
  },

  {
    code: 'terminal_reader_hardware_fault',
    description:
      'Reader can no longer accept payments as an unrecoverable hardware fault has been detected. Please reach out to Stripe support at https://support.stripe.com/contact/email and provide your reader’s serial number for replacement.',
  },

  {
    code: 'terminal_reader_invalid_location_for_activation',
    description:
      'Reader can’t be activated in the currently registered Location. Please register the reader to a new Location. See Manage locations for more information.',
  },

  {
    code: 'terminal_reader_invalid_location_for_payment',
    description:
      'Reader can’t take payments in the currently registered Location. Please register the reader to a new Location. See Manage locations for more information.',
  },

  {
    code: 'terminal_reader_offline',
    description:
      'Reader is currently offline, please ensure the reader is powered on and connected to the internet before retrying your request. Reference the integration guide for details on how to handle this error.',
  },

  {
    code: 'terminal_reader_timeout',
    description:
      'There was a timeout when sending this command to the reader. Please reference the integration guide for details on how to handle this error.',
  },

  {
    code: 'testmode_charges_only',
    description:
      'Your account has not been activated and can only make test charges. Activate your account in the Dashboard to begin processing live charges.',
  },

  {
    code: 'tls_version_unsupported',
    description:
      'Your integration is using an older version of TLS that is unsupported. You must be using TLS 1.2 or above.',
  },

  {
    code: 'token_already_used',
    description:
      'The token provided has already been used. You must create a new token before you can retry this request.',
  },

  {
    code: 'token_card_network_invalid',
    description:
      'Invalid card network parameter was provided in the card token object. Check our API documentation or the returned error message for more context.',
  },

  {
    code: 'token_in_use',
    description:
      'The token provided is currently being used in another request. This occurs if your integration is making duplicate requests simultaneously.',
  },

  {
    code: 'transfer_source_balance_parameters_mismatch',
    description:
      'When creating a Transfer, the payments parameter in source_balance should not be passed in when balance type is set to issuing.',
  },

  {
    code: 'transfers_not_allowed',
    description:
      'The requested transfer cannot be created. Contact us if you are receiving this error.',
  },

  {code: 'url_invalid', description: 'The URL provided is invalid.'},
]

export const RESOLVED_ERRORS = ERRORS.filter(error => !!error.belongsTo)
